<template>
    <b-modal :visible="value" @change="onVisibleChange" title="Auto-Pay" hide-footer>
        <div v-if="loading" class="text-center p-3">
            <b-spinner small /> Loading...
        </div>

        <template v-else>
            <b-alert :show="!autoPayAvailable" variant="warning" class="mb-2">
                Auto-pay is not enabled for this pharmacy.
            </b-alert>

            <!-- The disable path is always available for an already-enrolled account so it can be
                 unenrolled even after the pharmacy turns auto-pay off; only enrolling is gated on
                 availability. Keyed off the loaded enrollment (not the live checkbox) so toggling
                 off doesn't hide the Save button before the change can be saved. -->
            <template v-if="autoPayAvailable || wasEnrolled">
                <b-form-checkbox v-model="enabled" switch class="mb-3">
                    Enroll this account in auto-pay
                </b-form-checkbox>

                <b-form-group v-if="enabled && autoPayAvailable" label="Payment method">
                    <b-select v-model="stripeCardId" :options="cardOptions" />
                    <small v-if="!cards.length" class="text-muted">
                        The responsible patient has no saved cards on file. A card must be added before
                        enrolling in auto-pay.
                    </small>
                </b-form-group>

                <b-form-group v-if="enabled && autoPayAvailable" label="Maximum payment amount">
                    <b-form-input v-model.number="maxAmount" type="number" min="0.01" step="0.01"
                                  :state="maxAmountInvalid ? false : null"
                                  placeholder="Full statement balance"
                                  @input.native="captureMaxAmountBadInput" />
                    <b-form-invalid-feedback :force-show="maxAmountInvalid">
                        Enter a valid amount, or leave the field blank to charge the whole statement
                        balance.
                    </b-form-invalid-feedback>
                    <small class="text-muted">
                        Leave blank to charge the whole statement balance. Set an amount for a patient
                        on a payment arrangement — auto-pay then charges whichever is smaller, this
                        amount or the balance owed.
                    </small>
                </b-form-group>

                <div class="text-right">
                    <b-button size="sm" variant="primary" :disabled="saving || !canSave" @click="save">
                        Save
                    </b-button>
                </div>
            </template>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { ARAutoPayStatus, ARSavedCard, arSavedCardOptions } from "@/models/AR";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "ARAutoPayModal",
})
export default class ARAutoPayModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) accountId!: number | null;
    // Provided by the parent card, which already loaded the config to decide whether to show the
    // Auto-Pay button — passing it down avoids re-fetching config and state that can diverge.
    @Prop({ type: Boolean, default: false }) autoPayAvailable!: boolean;

    private loading = false;
    private saving = false;
    // Whether the current account's settings actually came back. Saving is gated on it so a failed
    // load can only be retried, never saved over the top of.
    private loaded = false;
    private enabled = false;
    // The account's persisted enrollment at load time. Drives whether the disable controls show, so
    // unchecking the box (setting enabled=false) doesn't hide Save before the change is saved.
    private wasEnrolled = false;
    private stripeCardId: string | null = null;
    // Typed as string too because clearing a `.number` bound input yields "", not null.
    private maxAmount: number | string | null = null;
    // Whether the browser rejected the last thing typed into the amount field as unparseable.
    private maxAmountBadInput = false;
    private cards: ARSavedCard[] = [];

    // The maximum as the API wants it: null for "no maximum, charge the full statement balance".
    get normalizedMaxAmount(): number | null {
        const entered = typeof this.maxAmount === "string" ? this.maxAmount.trim() : this.maxAmount;
        if (entered === null || entered === "") return null;
        const parsed = Number(entered);
        return Number.isFinite(parsed) ? parsed : null;
    }

    // Whether what's typed is a number at all. This has to be asked separately from
    // normalizedMaxAmount, because that getter reports both "blank" and "unparseable" as null — and
    // null is how the API spells "no maximum", so an unusable entry would otherwise save the account
    // as uncapped.
    get maxAmountInvalid(): boolean {
        // A number input never hands us the bad text: the browser reports what it cannot parse as an
        // empty value, which is indistinguishable from a deliberately cleared field. Only the input
        // event knows the difference, so that answer is captured there rather than derived here.
        if (this.maxAmountBadInput) return true;
        const entered = typeof this.maxAmount === "string" ? this.maxAmount.trim() : this.maxAmount;
        // Blank is not invalid — it is the deliberate way to say "no maximum".
        if (entered === null || entered === "") return false;
        return !Number.isFinite(Number(entered));
    }

    // Set from the input event's validity flag, which is true exactly when the browser could not
    // turn what was typed into a number (`1e999`, `1-2`, a lone `-`). Kept as state because the
    // flag lives on the event, not on the bound value.
    private captureMaxAmountBadInput(event: Event): void {
        this.maxAmountBadInput = (event.target as HTMLInputElement)?.validity?.badInput === true;
    }

    get cardOptions(): { value: string | null; text: string }[] {
        return arSavedCardOptions(this.cards);
    }

    get canSave(): boolean {
        // Nothing is saveable until this account's own settings have loaded — otherwise a transient
        // GET failure would let staff save a blank enrollment over a live one.
        if (!this.loaded) return false;
        // Unenrolling is always allowed; enrolling requires a selected card. The maximum is optional
        // — blank charges the full balance — but a zero or negative one would enroll the account
        // into never being charged, so it's rejected rather than saved.
        if (!this.enabled) return true;
        if (!this.stripeCardId) return false;
        if (this.maxAmountInvalid) return false;
        return this.normalizedMaxAmount === null || this.normalizedMaxAmount > 0;
    }

    @Watch("value")
    async onShow(visible: boolean): Promise<void> {
        if (!visible || !this.accountId) return;
        await this.load();
    }

    private async load(): Promise<void> {
        if (!this.accountId) return;

        // Reset before fetching. The modal is reused across accounts, so a failed load that left the
        // previous account's values in place would offer this account's Save button someone else's
        // enrollment, card and negotiated maximum.
        this.loading = true;
        this.loaded = false;
        this.enabled = false;
        this.wasEnrolled = false;
        this.stripeCardId = null;
        this.maxAmount = null;
        this.maxAmountBadInput = false;
        this.cards = [];

        try {
            const [status, cards] = await Promise.all([
                axios.get<ARAutoPayStatus>(`/AR/accounts/${this.accountId}/autopay`),
                axios.get<ARSavedCard[]>(`/AR/accounts/${this.accountId}/payment-methods`).catch(() => ({ data: [] })),
            ]);
            this.enabled = status.data?.enabled ?? false;
            this.wasEnrolled = this.enabled;
            this.stripeCardId = status.data?.stripeCardId ?? null;
            this.maxAmount = status.data?.maxAmount ?? null;
            this.cards = cards.data ?? [];
            this.loaded = true;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    async save(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.put(`/AR/accounts/${this.accountId}/autopay`, {
                enabled: this.enabled,
                stripeCardId: this.enabled ? this.stripeCardId : null,
                maxAmount: this.enabled ? this.normalizedMaxAmount : null,
            });
            this.$notification(NotificationOptions.success("Auto-pay updated."));
            this.$emit("saved");
            this.onVisibleChange(false);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }
}
</script>

<style scoped></style>
